import { useEffect } from 'react';
import ChatWidget from './components/ChatWidget/ChatWidget';
import { ChatProvider } from './context/ChatContext';
import { AuthProvider } from './context/AuthContext';
import { useInternalEventEmitter } from './hooks/useInternalEventEmitter';
import type { ChatConfig, TokenData } from './types/chat.types';
import { initializeAppInsights } from './services/appInsightsService';
import { createCustomWidgetConsole } from './utils/widgetConsole';
import './App.css';

const widgetConsole = createCustomWidgetConsole();
declare global {
  interface Window {
    widgetConsole: typeof widgetConsole;
  }
}

type Extension = {
  type: 'loginTracker';
  component: React.ComponentType;
};

type AppProps = {
  config: ChatConfig;
  getToken?: () => Promise<TokenData>;
  getEntraToken?: () => Promise<string | null>;
  startLogin?: () => Promise<any>;
  getOpcoContextData?: () => object;
  refreshToken?: () => Promise<string>;
  autoInputToTriage?: string;
  isLivechatAvailable?: () => boolean;
  extensions?: Extension[];
  lazyInit?: boolean;
};

const App: React.FC<AppProps> = ({
  config,
  getToken,
  getEntraToken,
  startLogin,
  getOpcoContextData,
  autoInputToTriage,
  isLivechatAvailable,
  extensions,
  lazyInit,
  refreshToken,
}) => {
  const internalEventEmitter = useInternalEventEmitter();
  const appInsightsConnectionString = import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING;

  // Initialize App Insights when component mounts with config
  useEffect(() => {
    if (appInsightsConnectionString && config.context) {
      initializeAppInsights(
        appInsightsConnectionString,
        config.context
      );
      createCustomWidgetConsole();
      widgetConsole.log("[App] App Insights initialized successfully");
    } else {
      console.warn(
        "[App] App Insights connection string or context missing, skipping initialization"
      );
    }
  }, []);

  return (
    <div className="chatbot-widget-root">
      <AuthProvider getToken={getToken} getEntraToken={getEntraToken} refreshToken={refreshToken}>
        <ChatProvider internalEventEmitter={internalEventEmitter} config={config}>
          {extensions?.map((ext, idx) => {
            const Component = ext.component;
            return <Component key={`${ext.type}-${idx}`} />;
          })}
          <ChatWidget
            config={config}
            startLogin={startLogin}
            autoInputToTriage={autoInputToTriage}
            isLivechatAvailable={isLivechatAvailable}
            getOpcoContextData={getOpcoContextData}
            lazyInit={lazyInit}
            refreshToken={refreshToken}
          />
        </ChatProvider>
      </AuthProvider>
    </div>
  );
};

export default App;
